import {createApi} from '@reduxjs/toolkit/query/react';
import {
  CourseDetails,
  GetCoursesResponse,
  TutorSignUpRequest,
  TutorSignUpResponse,
  TutorClass,
  GetTutorClassesResponse,
} from '../../types/tutorTypes';
import { TutorProfileResponse } from '../../features/profiles/types/profileTypes';
import { baseQueryWithAuth } from './baseQuery';
import { UpdateCoursePayload, UpdateTutorProfilePayload, ApiResponse } from '../../types/api';

export const tutorApi = createApi({
  reducerPath: 'tutorApi',
  baseQuery: baseQueryWithAuth,
  endpoints: builder => ({
    getTutorCourses: builder.query<CourseDetails[], {tutorId: number}>({
      query: ({tutorId}) => ({
        url: `/tutors/${tutorId}/courses`,
        method: 'GET',
        headers: {
          accept: '*/*',
        },
      }),
      transformResponse: (response: GetCoursesResponse) => response.data.arrayObj,
    }),
    signupTutor: builder.mutation<TutorSignUpResponse, TutorSignUpRequest>({
      query: body => ({
        url: '/auth/signup/tutor',
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
      }),
    }),
    getCourseDetails: builder.query<CourseDetails, {tutorId: number, courseId: number}>({
      query: ({tutorId, courseId}) => ({
        url: `/tutors/${tutorId}/courses/${courseId}`,
        method: 'GET',
        headers: {
          accept: '*/*',
        },
      }),
    }),
    updateCourse: builder.mutation<ApiResponse<CourseDetails>, {tutorId: number, courseId: number, courseData: UpdateCoursePayload}>({
      query: ({tutorId, courseId, courseData}) => ({
        url: `/tutors/${tutorId}/courses/${courseId}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: courseData,
      }),
    }),
    getTutorAllClasses: builder.query<TutorClass[], {tutorId: number}>({
      query: ({tutorId}) => ({
        url: `/tutors/${tutorId}/allclasses`,
        method: 'GET',
        headers: {
          accept: '*/*',
        },
      }),
      transformResponse: (response: GetTutorClassesResponse) => response.data.arrayObj,
    }),
    getTutorUpcomingClasses: builder.query<TutorClass[], {tutorId: number}>({
      query: ({tutorId}) => ({
        url: `/tutors/${tutorId}/upcomingclasses`,
        method: 'GET',
        headers: {
          accept: '*/*',
        },
      }),
      transformResponse: (response: GetTutorClassesResponse) => response.data.arrayObj,
    }),
    getTutorProfile: builder.query<TutorProfileResponse['data'], {tutorId: number}>({
      query: ({tutorId}) => ({
        url: `/tutors/${tutorId}`,
        method: 'GET',
        headers: {
          accept: '*/*',
        },
      }),
      transformResponse: (response: TutorProfileResponse) => response.data,
    }),
    updateTutorProfile: builder.mutation<ApiResponse<TutorProfileResponse['data']>, {tutorId: number, profileData: UpdateTutorProfilePayload}>({
      query: ({tutorId, profileData}) => ({
        url: `/tutors/${tutorId}`,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: profileData,
      }),
    }),
  }),
});

export const {
  useGetTutorCoursesQuery,
  useSignupTutorMutation,
  useGetCourseDetailsQuery,
  useUpdateCourseMutation,
  useGetTutorAllClassesQuery,
  useGetTutorUpcomingClassesQuery,
  useGetTutorProfileQuery,
  useUpdateTutorProfileMutation,
} = tutorApi;
